import React, { FC } from 'react';

import { useSelector } from 'react-redux';

import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { StateType } from '~/redux/types';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';

import GameInfoReports from '../../components/gameInfoReports/GameInfoReports';
import GameStats from '../../components/gameStats/GameStats';
import LineScore from '../../components/lineScore/LineScore';
import SeasonSeries from '../../components/seasonSeries/SeasonSeries';
import ShotsOnGoalByPeriod from '../../components/shotsOnGoalByPeriod/ShotsOnGoalByPeriod';

import { AdContainer, LoaderContainer, RightRailContainer } from './styles';

const RightRail: FC = () => {
    // Rather than fetching the right rail here, we will take it from Redux state
    // If we fetched the right rail here, we could potentially fetch it twice.
    // Since the landing page can route to two different components which both render this, depending on game state
    const rightRail = useSelector((state: StateType) => state.rightRail);

    const screenSize = useSizeWithThrottle();
    const isLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    const game = useSelector((state: StateType) => state.gameCenter);

    const seasonSeriesFullCoverageUrl = useOptionForLocale(rightRail.seasonSeriesFullCoverageUrl, true);

    // When there is no game information a full screen loader is already being shown, so we show nothing here
    if (!game.id) {
        return null;
    }

    // We don't check isInitialLoad of 'useGameCenterRightRailQuery' b/c when tabs switch this would be true.
    // We care if right rail info is present or not
    if (!rightRail?.gameInfo) {
        return (
            <LoaderContainer>
                <LoadingDisplay isLoading isInline />
            </LoaderContainer>
        );
    }

    const content = game ? (
        <>
            {rightRail.linescore && <LineScore game={game} linescore={rightRail.linescore} />}
            {window.__AD_SETTINGS__.mrecDivId && (
                <AdContainer>
                    <AdTag divId={window.__AD_SETTINGS__.mrecDivId} adType={AdType.MEDIUM_RECTANGLE} forceReload />
                </AdContainer>
            )}
            <ShotsOnGoalByPeriod game={game} shotsByPeriod={rightRail.shotsByPeriod} />
            {rightRail.teamGameStats && rightRail.teamGameStats.length > 0 && (
                <GameStats game={game} teamGameStats={rightRail.teamGameStats} />
            )}
            {rightRail?.seasonSeries && rightRail.seasonSeries.length > 0 && (
                <SeasonSeries
                    game={game}
                    seasonSeries={rightRail.seasonSeries}
                    seasonSeriesWins={rightRail.seasonSeriesWins}
                    seasonSeriesFullCoverageUrl={seasonSeriesFullCoverageUrl}
                />
            )}
            <GameInfoReports game={game} gameInfo={rightRail.gameInfo} gameReports={rightRail.gameReports} />
        </>
    ) : null;
    return isLarge ? <RightRailContainer>{content}</RightRailContainer> : content;
};

export default RightRail;
